import React, { useState, Children } from 'react';
import { MailIcon, PhoneIcon, MapPinIcon, ClockIcon, SendIcon, CheckCircleIcon } from 'lucide-react';
import { AnimatedSection, AnimatedChildren, AnimatedChild } from '../components/ui/AnimatedSection';
interface FormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
  privacy: boolean;
}
interface FormErrors {
  name?: string;
  company?: string;
  email?: string;
  message?: string;
  privacy?: string;
}
const contactInfo = [{
  icon: MailIcon,
  label: 'E-Mail',
  value: 'hello@fooblemedia.de',
  href: 'mailto:hello@fooblemedia.de'
}, {
  icon: PhoneIcon,
  label: 'Telefon',
  value: '+49 89 123 456 789',
  href: 'tel:+4989123456789'
}, {
  icon: MapPinIcon,
  label: 'Standort',
  value: 'DACH-Raum',
  href: null
}, {
  icon: ClockIcon,
  label: 'Reaktionszeit',
  value: 'Innerhalb von 24 Stunden',
  href: null
}];
export function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    privacy: false
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Bitte geben Sie Ihren Namen ein.';
    if (!formData.company.trim()) newErrors.company = 'Bitte geben Sie Ihr Unternehmen ein.';
    if (!formData.email.trim()) newErrors.email = 'Bitte geben Sie Ihre E-Mail-Adresse ein.';else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    if (!formData.message.trim()) newErrors.message = 'Bitte geben Sie eine Nachricht ein.';
    if (!formData.privacy) newErrors.privacy = 'Bitte akzeptieren Sie die Datenschutzerklärung.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name as keyof FormErrors]) setErrors(prev => ({
      ...prev,
      [name]: undefined
    }));
  };
  return <div className="w-full" data-id="element-320">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0C0C0F]" data-id="element-321">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-322">
          <AnimatedSection data-id="element-323">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tightest gradient-text max-w-3xl" data-id="element-324">
              Kontakt
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.1} data-id="element-325">
            <p className="mt-6 text-lg md:text-xl text-white/50 max-w-2xl" data-id="element-326">
              Lassen Sie uns ins Gespräch kommen. Wir freuen uns darauf, mehr
              über Ihr Projekt zu erfahren.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 md:py-32 bg-[#09090B]" data-id="element-327">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12" data-id="element-328">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24" data-id="element-329">
            {/* Form */}
            <AnimatedSection data-id="element-330">
              {isSubmitted ? <div className="p-8 bg-white/5 border border-white/10 rounded-2xl text-center" data-id="element-331">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6" data-id="element-332">
                    <CheckCircleIcon className="w-8 h-8 text-green-500" data-id="element-333" />
                  </div>
                  <h3 className="text-2xl font-bold text-white" data-id="element-334">
                    Vielen Dank für Ihre Nachricht!
                  </h3>
                  <p className="mt-4 text-white/50" data-id="element-335">
                    Wir haben Ihre Anfrage erhalten und melden uns innerhalb von
                    24 Stunden bei Ihnen.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-336">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-337">
                    <div data-id="element-338">
                      <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2" data-id="element-339">
                        Name *
                      </label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg bg-white/5 border ${errors.name ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#E8781A]/20 focus:border-[#E8781A]/50 transition-colors`} placeholder="Ihr Name" data-id="element-340" />
                      {errors.name && <p className="mt-1 text-sm text-red-500" data-id="element-341">
                          {errors.name}
                        </p>}
                    </div>
                    <div data-id="element-342">
                      <label htmlFor="company" className="block text-sm font-medium text-white/80 mb-2" data-id="element-343">
                        Unternehmen *
                      </label>
                      <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg bg-white/5 border ${errors.company ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#E8781A]/20 focus:border-[#E8781A]/50 transition-colors`} placeholder="Ihr Unternehmen" data-id="element-344" />
                      {errors.company && <p className="mt-1 text-sm text-red-500" data-id="element-345">
                          {errors.company}
                        </p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-346">
                    <div data-id="element-347">
                      <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2" data-id="element-348">
                        E-Mail *
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 rounded-lg bg-white/5 border ${errors.email ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#E8781A]/20 focus:border-[#E8781A]/50 transition-colors`} placeholder="ihre@email.de" data-id="element-349" />
                      {errors.email && <p className="mt-1 text-sm text-red-500" data-id="element-350">
                          {errors.email}
                        </p>}
                    </div>
                    <div data-id="element-351">
                      <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2" data-id="element-352">
                        Telefon (optional)
                      </label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#E8781A]/20 focus:border-[#E8781A]/50 transition-colors" placeholder="+49 123 456789" data-id="element-353" />
                    </div>
                  </div>
                  <div data-id="element-354">
                    <label htmlFor="message" className="block text-sm font-medium text-white/80 mb-2" data-id="element-355">
                      Nachricht *
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={6} className={`w-full px-4 py-3 rounded-lg bg-white/5 border ${errors.message ? 'border-red-500' : 'border-white/10'} text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#E8781A]/20 focus:border-[#E8781A]/50 transition-colors resize-none`} placeholder="Erzählen Sie uns von Ihrem Projekt..." data-id="element-356" />
                    {errors.message && <p className="mt-1 text-sm text-red-500" data-id="element-357">
                        {errors.message}
                      </p>}
                  </div>
                  <div data-id="element-358">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-359">
                      <input type="checkbox" name="privacy" checked={formData.privacy} onChange={handleChange} className="mt-1 w-4 h-4 rounded border-white/10 bg-white/5 text-[#E8781A] focus:ring-[#E8781A]/20" data-id="element-360" />
                      <span className="text-sm text-white/50" data-id="element-361">
                        Ich habe die{' '}
                        <a href="/datenschutz" className="text-white underline hover:no-underline" data-id="element-362">
                          Datenschutzerklärung
                        </a>{' '}
                        gelesen und akzeptiere diese. *
                      </span>
                    </label>
                    {errors.privacy && <p className="mt-1 text-sm text-red-500" data-id="element-363">
                        {errors.privacy}
                      </p>}
                  </div>
                  <button type="submit" disabled={isSubmitting} className="inline-flex items-center justify-center w-full md:w-auto px-8 py-4 bg-gradient-to-r from-[#E8781A] to-[#a855f7] text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-364">
                    {isSubmitting ? <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-365">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-366"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-367"></path>
                        </svg>
                        Wird gesendet...
                      </> : <>
                        Nachricht senden
                        <SendIcon className="ml-2 w-4 h-4" data-id="element-368" />
                      </>}
                  </button>
                </form>}
            </AnimatedSection>

            {/* Contact Info */}
            <div data-id="element-369">
              <AnimatedChildren className="space-y-6" staggerDelay={0.1} data-id="element-370">
                {contactInfo.map(info => <AnimatedChild key={info.label} data-id="element-371">
                    <div className="p-6 bg-white/5 border border-white/10 rounded-xl" data-id="element-372">
                      <div className="flex items-start gap-4" data-id="element-373">
                        <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0" data-id="element-374">
                          <info.icon className="w-5 h-5 text-[#E8781A]" data-id="element-375" />
                        </div>
                        <div data-id="element-376">
                          <p className="text-sm text-white/40" data-id="element-377">{info.label}</p>
                          {info.href ? <a href={info.href} className="text-lg font-medium text-white hover:text-[#E8781A] transition-colors" data-id="element-378">
                              {info.value}
                            </a> : <p className="text-lg font-medium text-white" data-id="element-379">
                              {info.value}
                            </p>}
                        </div>
                      </div>
                    </div>
                  </AnimatedChild>)}
              </AnimatedChildren>

              <AnimatedSection delay={0.4} className="mt-8" data-id="element-380">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden relative" data-id="element-381">
                  <img src="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80" alt="DACH-Raum" className="w-full h-full object-cover" data-id="element-382" />
                  <div className="absolute inset-0 bg-black/40" data-id="element-383" />
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </div>;
}